// src/components/dashboards/PaperEvaluatorDashboard.jsx
// Paper Evaluator Dashboard — Full Screen Workstation
// Design: White cards · Dark slate · rgb(49,154,185) accent · Tailwind CSS

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../shared/DashboardLayout';
import BookletViewer from '../shared/BookletViewer';
import { paperEvaluatorAPI } from '../../services/api';
import { notify, confirmDialog } from '../../utils/notify';

const ACCENT = 'rgb(49,154,185)';

const getFullUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const backendBase = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');
  return `${backendBase}${url}`;
};

// Build CORS-safe proxy URL for evaluator PDF access
const getProxyPdfUrl = (assignmentId) => {
  const base = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  return `${base}/auth/coe/proxy-pdf/${assignmentId}/`;
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export default function PaperEvaluatorDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState({ pending: 0, completed: 0, total: 0 });
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Active evaluation session
  const [activeEval, setActiveEval] = useState(null);
  const [qp, setQp] = useState(null);
  const [marks, setMarks] = useState({}); // { section_id: marks }
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);
  const [showBooklet, setShowBooklet] = useState(false); // BookletViewer open/close
  const [showQPCollapse, setShowQPCollapse] = useState(true); // Question paper section collapse state

  // PDF JS state
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [zoom, setZoom] = useState(1.0);
  const [pageFlipClass, setPageFlipClass] = useState('');

  // Annotation states
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushColor, setBrushColor] = useState('#ef4444');
  const [brushSize, setBrushSize] = useState(3);
  const [history, setHistory] = useState({}); // { pageNum: [strokes] }
  const [currentStroke, setCurrentStroke] = useState([]);

  // Video recording states
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingError, setRecordingError] = useState('');
  const [videoBlob, setVideoBlob] = useState(null);
  const [streams, setStreams] = useState([]);

  const pdfCanvasRef = useRef(null);
  const drawCanvasRef = useRef(null);
  const containerRef = useRef(null);

  // Load PDF.js CDN dynamically
  useEffect(() => {
    if (window.pdfjsLib) return;
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js';
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';
      console.log('PDFJS Loaded');
    };
    document.body.appendChild(script);
  }, []);

  const loadAssignments = async () => {
    setLoading(true);
    try {
      const data = await paperEvaluatorAPI.getAssignments();
      const list = Array.isArray(data) ? data : (data.results || []);
      setAssignments(list);

      // Compute simple stats — handle both old COMPLETED and new SUBMITTED status values
      const completed = list.filter(a => a.status === 'SUBMITTED' || a.status === 'COMPLETED').length;
      const pending = list.length - completed;
      setStats({ pending, completed, total: list.length });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssignments();
  }, []);

  // Filter list
  const filtered = assignments.filter(a => {
    const q = search.toLowerCase();
    const student = (a.student_name || '').toLowerCase();
    const roll = (a.student_id_num || '').toLowerCase();
    const subject = (a.bundle_details?.schedule_details?.subject || '').toLowerCase();
    return student.includes(q) || roll.includes(q) || subject.includes(q);
  });

  // Start evaluating
  const handleStartEvaluation = async (evaluation) => {
    setActiveEval(evaluation);
    setQp(evaluation.bundle_details?.question_paper_details);
    // backend stores remarks in 'comments' field
    setRemarks(evaluation.comments || evaluation.remarks || '');
    setPageNum(1);
    setHistory(evaluation.annotations || {});
    setVideoBlob(null);
    setRecordedChunks([]);
    setRecordingError('');

    // Pre-populate marks from section_marks dict (keyed by str(section_id))
    const sectionMarksDict = (evaluation.section_marks && typeof evaluation.section_marks === 'object' && !Array.isArray(evaluation.section_marks))
      ? evaluation.section_marks
      : {};
    const initialMarks = {};
    evaluation.bundle_details?.question_paper_details?.sections?.forEach(sec => {
      const existing = sectionMarksDict[String(sec.id)];
      initialMarks[sec.id] = existing !== undefined ? String(existing) : '';
    });
    setMarks(initialMarks);

    // Initialize combined screen and webcam recording automatically
    let displayStream;
    let userStream;
    let combinedStream;
    let activeDrawingLoop = { active: true };

    try {
      displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          width: 1280,
          height: 720,
          frameRate: 15
        },
        audio: false
      });
    } catch (err) {
      console.error('Screen recording initialization failed:', err);
      setRecordingError('Screen recording permission is mandatory to start evaluation.');
      setActiveEval(null);
      return;
    }

    try {
      userStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: 320,
          height: 240,
          frameRate: 15
        },
        audio: true
      });
    } catch (err) {
      console.warn('Webcam/Audio not available or permitted. Using screen share only.', err);
    }

    if (displayStream && userStream) {
      // Create offscreen video elements to draw onto canvas
      const screenVideo = document.createElement('video');
      screenVideo.srcObject = displayStream;
      screenVideo.muted = true;
      screenVideo.autoplay = true;
      screenVideo.playsInline = true;

      const webcamVideo = document.createElement('video');
      webcamVideo.srcObject = userStream;
      webcamVideo.muted = true;
      webcamVideo.autoplay = true;
      webcamVideo.playsInline = true;

      // Create offscreen canvas
      const canvas = document.createElement('canvas');
      canvas.width = 1280;
      canvas.height = 720;
      const ctx = canvas.getContext('2d');

      // Wait for videos to load metadata & start playing
      await Promise.all([
        new Promise((resolve) => {
          screenVideo.onloadedmetadata = () => {
            screenVideo.play().then(resolve).catch(resolve);
          };
          // Fallback if event already fired
          if (screenVideo.readyState >= 2) resolve();
        }),
        new Promise((resolve) => {
          webcamVideo.onloadedmetadata = () => {
            webcamVideo.play().then(resolve).catch(resolve);
          };
          // Fallback if event already fired
          if (webcamVideo.readyState >= 2) resolve();
        })
      ]);

      // Draw loop
      const drawCombined = () => {
        if (!activeDrawingLoop.active) return;
        ctx.drawImage(screenVideo, 0, 0, canvas.width, canvas.height);
        
        // Webcam Picture-in-Picture (bottom right)
        const camW = 240;
        const camH = 180;
        const padding = 20;
        const x = canvas.width - camW - padding;
        const y = canvas.height - camH - padding;

        ctx.fillStyle = '#000';
        ctx.fillRect(x - 2, y - 2, camW + 4, camH + 4);
        ctx.drawImage(webcamVideo, x, y, camW, camH);

        requestAnimationFrame(drawCombined);
      };
      drawCombined();

      combinedStream = canvas.captureStream(15);
      // Add webcam audio tracks if present
      userStream.getAudioTracks().forEach(track => combinedStream.addTrack(track));
      setStreams([displayStream, userStream]);
    } else {
      combinedStream = displayStream;
      setStreams([displayStream]);
    }

    try {
      const recorder = new MediaRecorder(combinedStream, {
        mimeType: 'video/webm;codecs=vp8'
      });

      const chunks = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunks.push(e.data);
      };

      recorder.onstop = () => {
        activeDrawingLoop.active = false;
        const blob = new Blob(chunks, { type: 'video/webm' });
        setVideoBlob(blob);
      };

      setMediaRecorder(recorder);
      recorder.start(1000); // chunk every 1s
      setIsRecording(true);
    } catch (err) {
      console.error('Recorder setup failed:', err);
      setRecordingError('Failed to initialize combined media recorder.');
      if (displayStream) displayStream.getTracks().forEach(t => t.stop());
      if (userStream) userStream.getTracks().forEach(t => t.stop());
      setActiveEval(null);
      return;
    }

    // Load PDF Document — prefer combined PDF, fall back to first individual answer sheet
    const pdfUrl = evaluation.student_combined_pdf
      || evaluation.answer_sheets?.[0]?.file_url
      || evaluation.answer_sheets?.[0]?.file
      || null;

    if (pdfUrl) {
      setPdfLoading(true);
      const url = getFullUrl(pdfUrl);
      try {
        const token = localStorage.getItem('access_token');
        const docParams = { url: url };
        if (token) {
          docParams.httpHeaders = {
            'Authorization': `Bearer ${token}`
          };
        }
        const loadingTask = window.pdfjsLib.getDocument(docParams);
        const pdf = await loadingTask.promise;
        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);
      } catch (err) {
        console.error('Failed to load PDF:', err);
      } finally {
        setPdfLoading(false);
      }
    }
  };

  // Render PDF Page onto canvas
  useEffect(() => {
    if (!pdfDoc || !window.pdfjsLib) return;

    let isCancelled = false;

    const renderPage = async () => {
      try {
        const page = await pdfDoc.getPage(pageNum);
        if (isCancelled) return;

        const canvas = pdfCanvasRef.current;
        const drawCanvas = drawCanvasRef.current;
        if (!canvas || !drawCanvas) return;

        const ctx = canvas.getContext('2d');
        const drawCtx = drawCanvas.getContext('2d');

        const viewport = page.getViewport({ scale: zoom });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        drawCanvas.width = viewport.width;
        drawCanvas.height = viewport.height;

        const renderContext = {
          canvasContext: ctx,
          viewport: viewport
        };

        await page.render(renderContext).promise;

        if (isCancelled) return;

        // Clear draw canvas
        drawCtx.clearRect(0, 0, drawCanvas.width, drawCanvas.height);

        // Draw annotation history for this page
        const pageStrokes = history[pageNum] || [];
        drawCtx.strokeStyle = brushColor;
        drawCtx.lineWidth = brushSize;
        drawCtx.lineCap = 'round';
        drawCtx.lineJoin = 'round';

        pageStrokes.forEach(stroke => {
          if (stroke.length === 0) return;
          drawCtx.beginPath();
          drawCtx.moveTo(stroke[0].x * zoom, stroke[0].y * zoom);
          for (let i = 1; i < stroke.length; i++) {
            drawCtx.lineTo(stroke[i].x * zoom, stroke[i].y * zoom);
          }
          drawCtx.stroke();
        });

      } catch (err) {
        console.error('Page render error:', err);
      }
    };

    renderPage();

    return () => {
      isCancelled = true;
    };
  }, [pdfDoc, pageNum, zoom, history]);

  // Handle Drawing on Canvas
  const getCoordinates = (e) => {
    const canvas = drawCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    // Normalize coordinates based on current zoom
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);
    return {
      x: (clientX - rect.left) / zoom,
      y: (clientY - rect.top) / zoom
    };
  };

  const startDrawing = (e) => {
    setIsDrawing(true);
    const coords = getCoordinates(e);
    setCurrentStroke([coords]);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);

    // Draw locally in real time
    ctx.strokeStyle = brushColor;
    ctx.lineWidth = brushSize * zoom;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    ctx.beginPath();
    const lastCoords = currentStroke[currentStroke.length - 1];
    ctx.moveTo(lastCoords.x * zoom, lastCoords.y * zoom);
    ctx.lineTo(coords.x * zoom, coords.y * zoom);
    ctx.stroke();

    setCurrentStroke(prev => [...prev, coords]);
  };

  const autoSaveAnnotations = async (updatedHistory) => {
    if (!activeEval) return;
    try {
      await paperEvaluatorAPI.updateEvaluation(activeEval.id, {
        annotations: updatedHistory
      });
    } catch (err) {
      console.error('[autoSaveAnnotations] Failed to auto-save:', err);
    }
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    if (currentStroke.length > 0) {
      const updated = {
        ...history,
        [pageNum]: [...(history[pageNum] || []), currentStroke]
      };
      setHistory(updated);
      autoSaveAnnotations(updated);
    }
    setCurrentStroke([]);
  };

  const undoStroke = () => {
    const pageStrokes = history[pageNum] || [];
    if (pageStrokes.length === 0) return;
    const updated = {
      ...history,
      [pageNum]: pageStrokes.slice(0, -1)
    };
    setHistory(updated);
    autoSaveAnnotations(updated);
  };

  const clearAnnotations = () => {
    const updated = {
      ...history,
      [pageNum]: []
    };
    setHistory(updated);
    autoSaveAnnotations(updated);
  };

  // Live validation calculations
  const calculateTotalEntered = () => {
    let sum = 0;
    qp?.sections?.forEach(sec => {
      const val = parseFloat(marks[sec.id]);
      if (!isNaN(val)) sum += val;
    });
    return sum;
  };

  const getMarksErrors = () => {
    const errors = {};
    qp?.sections?.forEach(sec => {
      const val = parseFloat(marks[sec.id]);
      if (marks[sec.id] !== '' && isNaN(val)) {
        errors[sec.id] = 'Must be a number';
      } else if (!isNaN(val)) {
        const attempt = sec.attempt_questions || sec.total_questions;
        const maxMarks = attempt * sec.marks_per_q;
        if (val < 0) {
          errors[sec.id] = 'Marks cannot be negative';
        } else if (val > maxMarks) {
          errors[sec.id] = `Cannot exceed section max: ${maxMarks} marks`;
        }
      }
    });
    return errors;
  };

  const marksErrors = getMarksErrors();
  const hasErrors = Object.keys(marksErrors).length > 0;
  const isAllFilled = qp?.sections?.every(sec => marks[sec.id] !== '') && remarks.trim() !== '';

  // Page switcher with simulated turn animation
  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages) return;
    setPageFlipClass('animate-pageTurn');
    setTimeout(() => {
      setPageNum(nextPage);
      setPageFlipClass('');
    }, 250);
  };

  // Finalize Submission
  const handleFinalize = async () => {
    if (hasErrors || !isAllFilled) {
      notify.error('Please fill all sections with valid marks and add remarks.');
      return;
    }

    if (isRecording) {
      notify.info('Please complete screen recording first.');
      return;
    }

    if (!videoBlob) {
      notify.success('Recording is missing. Screen recording is mandatory.');
      return;
    }

    setSaving(true);
    try {
      // 1. Submit Marks — send array of {section_id, marks}; backend translates
      const marksPayload = qp?.sections?.map(sec => ({
        section_id: sec.id,
        marks: parseFloat(marks[sec.id])
      }));

      await paperEvaluatorAPI.updateEvaluation(activeEval.id, {
        marks: marksPayload,
        remarks: remarks,
        status: 'COMPLETED'  // backend maps COMPLETED → SUBMITTED
      });

      // 2. Upload Video — field name must match backend
      const formData = new FormData();
      formData.append('video_recording', videoBlob, 'session_recording.webm');
      await paperEvaluatorAPI.uploadVideo(activeEval.id, formData);

      notify.success('Evaluation finalized and session video uploaded successfully.');
      setActiveEval(null);
      setPdfDoc(null);
      loadAssignments();
    } catch (err) {
      notify.error(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Failed to finalize evaluation.');
    } finally {
      setSaving(false);
    }
  };

  // Save draft marks
  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const marksPayload = qp?.sections?.map(sec => ({
        section_id: sec.id,
        marks: marks[sec.id] !== '' ? parseFloat(marks[sec.id]) : null
      })).filter(item => item.marks !== null);

      await paperEvaluatorAPI.updateEvaluation(activeEval.id, {
        marks: marksPayload,
        remarks: remarks,
        status: 'IN_PROGRESS'  // backend maps IN_PROGRESS → SAVED
      });

      notify.success('Draft saved successfully.');
    } catch (err) {
      notify.error(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Failed to save draft.');
    } finally {
      setSaving(false);
    }
  };

  // Stop screen recording manually
  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      if (streams && streams.length > 0) {
        streams.forEach(s => {
          if (s && typeof s.getTracks === 'function') {
            s.getTracks().forEach(track => track.stop());
          }
        });
      }
      setIsRecording(false);
    }
  };

  return (
  <DashboardLayout title={`Paper Evaluator Workspace — ${user?.first_name || 'Evaluator'}`}>
    {activeEval ? (
      // ── WORKSPACE VIEW — full-screen fixed overlay ──────────────────────
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 1000 }}
        className="flex flex-col xl:flex-row gap-3 bg-slate-900 text-slate-100 overflow-hidden p-3"
      >
          
          {/* Left panel: Info & Marks Form */}
          <div className="w-full xl:w-96 flex-shrink-0 flex flex-col justify-between bg-white text-slate-800 rounded-xl p-5 border border-slate-200/80 overflow-y-auto space-y-6 max-h-full shadow-sm">
            <div className="space-y-6">
              <div>
                <span className="bg-[#319ab9] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                  EVALUATION WORKSPACE
                </span>
                <h3 className="text-base font-extrabold text-slate-900 mt-2 truncate">{activeEval.student_name}</h3>
                <p className="text-xs text-slate-500 mt-1">Roll No: {activeEval.student_id_num}</p>
                <p className="text-xs text-slate-500">Subject: {activeEval.bundle_details?.schedule_details?.subject}</p>
              </div>

              {/* Recording Status Banner */}
              <div className={`p-3.5 rounded-xl border flex items-center justify-between gap-3 text-xs ${
                isRecording ? 'bg-red-50 border-red-200 text-red-700' :
                videoBlob ? 'bg-green-50 border-green-200 text-green-700' :
                'bg-slate-50 border-slate-200 text-slate-600'
              }`}>
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${isRecording ? 'bg-red-500 animate-pulse' : videoBlob ? 'bg-green-500' : 'bg-slate-400'}`} />
                  <span className="font-bold">
                    {isRecording ? '🔴 Recording Screen' : videoBlob ? '✓ Session Video Recorded' : 'No Recording'}
                  </span>
                </div>
                {isRecording && (
                  <button
                    onClick={stopRecording}
                    className="bg-red-600 hover:bg-red-700 text-white font-bold px-2.5 py-1 rounded-lg text-[10px] transition-colors"
                  >
                    ⏹ Stop
                  </button>
                )}
              </div>

              {/* Collapsible Question Paper */}
              <div className="border border-slate-200 rounded-xl overflow-hidden bg-slate-50">
                <button
                  onClick={() => setShowQPCollapse(!showQPCollapse)}
                  className="w-full px-3.5 py-2.5 flex items-center justify-between text-xs font-bold text-slate-700 hover:bg-slate-100 transition-colors uppercase tracking-wider"
                >
                  <span className="flex items-center gap-1.5">📋 Question Paper</span>
                  <span>{showQPCollapse ? '▲ Hide' : '▼ View'}</span>
                </button>
                
                {showQPCollapse && (
                  <div className="p-3 border-t border-slate-200 space-y-4 max-h-72 overflow-y-auto bg-white">
                    {qp?.sections && qp.sections.length > 0 ? (
                      qp.sections.map((sec, idx) => (
                        <div key={sec.id || idx} className="space-y-1.5">
                          <div className="font-extrabold text-[11px] text-slate-800 border-b pb-1 border-slate-100 flex flex-col">
                            <span>{sec.title} {sec.instructions ? ` - ${sec.instructions}` : ''}</span>
                            <span className="text-[9px] text-slate-400 font-normal mt-0.5">
                              ({sec.total_questions} questions, attempt {sec.attempt_questions || 'all'}, {sec.marks_per_q} marks each)
                            </span>
                          </div>
                          <ul className="space-y-2 text-[11px] text-slate-600 pl-0.5">
                            {sec.questions && sec.questions.length > 0 ? (
                              sec.questions.map((q, qidx) => (
                                <li key={q.id || qidx} className="bg-slate-50 p-2 rounded border border-slate-100">
                                  <div className="font-semibold text-slate-700">Q{qidx + 1}. {q.text}</div>
                                  {q.options && Array.isArray(q.options) && q.options.length > 0 && (
                                    <div className="mt-1 grid grid-cols-2 gap-1 pl-1.5 text-[10px] text-slate-500">
                                      {q.options.map((opt, oidx) => (
                                        <div key={oidx}>• {opt}</div>
                                      ))}
                                    </div>
                                  )}
                                  <div className="text-[9px] text-slate-400 mt-1 font-medium flex items-center gap-2">
                                    <span>Marks: {q.marks || sec.marks_per_q}</span>
                                    <span>|</span>
                                    <span>Type: {q.question_type}</span>
                                  </div>
                                </li>
                              ))
                            ) : (
                              <li className="text-[10px] text-slate-400 italic">No questions added in this section.</li>
                            )}
                          </ul>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-slate-500 italic">No sections or questions available in this paper.</p>
                    )}
                  </div>
                )}
              </div>

              {/* Marks Entry Table */}
              <div className="space-y-4">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Section-wise Marks</div>
                <div className="space-y-3">
                  {qp?.sections?.map(sec => {
                    const attempt = sec.attempt_questions || sec.total_questions;
                    const maxMarks = attempt * sec.marks_per_q;
                    return (
                      <div key={sec.id} className="space-y-1.5 bg-slate-50 p-3 rounded-xl border border-slate-200/60">
                        <div className="flex justify-between items-center text-xs">
                          <span className="font-bold text-slate-800">{sec.title}</span>
                          <span className="text-slate-500">Max Marks: {maxMarks}</span>
                        </div>
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          placeholder={`Enter marks (max ${maxMarks})`}
                          value={marks[sec.id] || ''}
                          onChange={e => setMarks(prev => ({ ...prev, [sec.id]: e.target.value }))}
                          className="w-full bg-white border border-slate-200 rounded-lg p-2 text-sm text-slate-950 focus:outline-none focus:ring-1 focus:ring-[#319ab9]"
                        />
                        {marksErrors[sec.id] && (
                          <div className="text-[10px] text-red-500 font-semibold mt-1">⚠️ {marksErrors[sec.id]}</div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="flex justify-between items-center bg-slate-50 p-3 rounded-xl border border-slate-200">
                  <span className="text-xs font-bold text-slate-500">LIVE SUM</span>
                  <span className="text-lg font-black text-slate-900">{calculateTotalEntered()} Marks</span>
                </div>
              </div>

              {/* Remarks Box */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Evaluation remarks / feedback *</label>
                <textarea
                  className="w-full bg-white border border-slate-200 rounded-xl p-3 text-sm text-slate-950 focus:outline-none focus:ring-1 focus:ring-[#319ab9]"
                  rows={3}
                  placeholder="Enter comments..."
                  value={remarks}
                  onChange={e => setRemarks(e.target.value)}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3 pt-4 border-t border-slate-200">
              <div className="flex gap-2">
                <button
                  onClick={handleSaveDraft}
                  disabled={saving}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-2 rounded-xl text-xs transition-colors border border-slate-200"
                >
                  💾 Save Draft
                </button>
                <button
                  onClick={() => {
                     stopRecording();
                     setActiveEval(null);
                     setPdfDoc(null);
                  }}
                  className="bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 font-bold px-3 py-2 rounded-xl text-xs transition-colors"
                >
                  Exit
                </button>
              </div>
              <button
                onClick={handleFinalize}
                disabled={saving || hasErrors || !isAllFilled || isRecording || !videoBlob}
                className="w-full bg-[#319ab9] hover:bg-[#28849f] disabled:bg-slate-100 disabled:text-slate-400 disabled:border-slate-200 text-white font-bold py-2.5 rounded-xl text-xs transition-colors disabled:opacity-50 border border-transparent"
              >
                {saving ? 'Saving...' : '✓ Finalize Evaluation & Upload'}
              </button>
              {isRecording && (
                <p className="text-[10px] text-center text-amber-600 font-medium">⚠️ Stop screen recording to enable finalizing.</p>
              )}
            </div>
          </div>

          {/* Right panel: Answer Sheet Viewer */}
          <div className="flex-1 min-w-0 flex flex-col bg-slate-950 rounded-xl border border-slate-800/80 overflow-hidden relative min-h-0">

            {/* Toolbar */}
            <div className="bg-slate-900 px-4 py-2.5 border-b border-slate-800 flex items-center justify-between gap-3 flex-wrap flex-shrink-0">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handlePageChange(pageNum - 1)}
                  disabled={pageNum <= 1}
                  className="bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-white p-2 rounded-lg text-xs transition-colors"
                >
                  ◀ Prev Page
                </button>
                <span className="text-xs text-slate-400 font-medium">Page {pageNum} of {totalPages}</span>
                <button
                  onClick={() => handlePageChange(pageNum + 1)}
                  disabled={pageNum >= totalPages}
                  className="bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-white p-2 rounded-lg text-xs transition-colors"
                >
                  Next Page ▶
                </button>
              </div>

              {/* Annotation Tools */}
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1.5 bg-slate-800 p-1.5 rounded-lg border border-slate-700">
                  <button
                    onClick={() => setBrushColor('#ef4444')}
                    className={`w-5 h-5 rounded-full bg-red-500 border ${brushColor === '#ef4444' ? 'border-white scale-110' : 'border-transparent'}`}
                    title="Red Pen"
                  />
                  <button
                    onClick={() => setBrushColor('#10b981')}
                    className={`w-5 h-5 rounded-full bg-emerald-500 border ${brushColor === '#10b981' ? 'border-white scale-110' : 'border-transparent'}`}
                    title="Green Pen"
                  />
                </div>
                <button
                  onClick={undoStroke}
                  className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-2.5 py-1.5 rounded-lg text-xs font-semibold"
                  title="Undo last stroke"
                >
                  ↩ Undo
                </button>
                <button
                  onClick={clearAnnotations}
                  className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-2.5 py-1.5 rounded-lg text-xs font-semibold"
                >
                  ✕ Clear Page
                </button>
              </div>

              {/* Zoom + Open booklet */}
              <div className="flex items-center gap-2">
                <button onClick={() => setZoom(z => Math.max(0.6, z - 0.15))} className="bg-slate-800 hover:bg-slate-700 text-white w-8 h-8 rounded-lg text-sm font-bold flex items-center justify-center">−</button>
                <span className="text-xs text-slate-400 font-medium w-12 text-center">{Math.round(zoom * 100)}%</span>
                <button onClick={() => setZoom(z => Math.min(2.5, z + 0.15))} className="bg-slate-800 hover:bg-slate-700 text-white w-8 h-8 rounded-lg text-sm font-bold flex items-center justify-center">+</button>
                <button
                  onClick={() => setShowBooklet(true)}
                  className="bg-[#319ab9] hover:bg-[#28849f] text-white font-bold px-3 py-1.5 rounded-lg text-xs transition-colors ml-2 whitespace-nowrap"
                  title="Open full-screen booklet viewer"
                >
                  📖 View Answer Sheet
                </button>
              </div>
            </div>

            {/* Canvas Area (inline fallback viewer) */}
            <div className="flex-1 min-h-0 overflow-auto p-4 flex justify-center items-start bg-slate-900/60" ref={containerRef}>
              {pdfLoading ? (
                <div className="text-slate-400 py-12">Loading PDF document pages...</div>
              ) : !pdfDoc ? (
                <div className="text-slate-500 py-12 flex flex-col items-center gap-4">
                  <div className="text-4xl">📋</div>
                  <div className="text-center">
                    {activeEval?.student_combined_pdf ? (
                      <>
                        <p className="font-semibold text-slate-300 mb-3">
                          Combined evaluation document available.
                        </p>
                        <button
                          onClick={() => setShowBooklet(true)}
                          className="bg-[#319ab9] hover:bg-[#28849f] text-white font-bold px-5 py-2.5 rounded-xl text-sm transition-colors"
                        >
                          📖 Open Booklet Viewer
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="font-semibold text-slate-400 mb-2">No evaluation document available.</p>
                        {activeEval?.answer_sheets?.length > 0 ? (
                          <div className="space-y-2">
                            <p className="text-xs text-slate-500 mb-2">Individual answer sheets (COE needs to generate the combined document):</p>
                            {activeEval.answer_sheets.map(sheet => (
                              <a
                                key={sheet.id}
                                href={getFullUrl(sheet.file_url || sheet.file)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block text-[#319ab9] underline text-xs hover:text-[#28849f]"
                              >
                                📎 {sheet.file_name || `Answer Sheet ${sheet.id}`}
                              </a>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-slate-600">No answer sheets uploaded for this student yet.</p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className={`relative border border-slate-800 shadow-2xl transition-all duration-300 transform ${pageFlipClass}`}>
                  <canvas ref={pdfCanvasRef} className="block" />
                  <canvas
                    ref={drawCanvasRef}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                    className="absolute top-0 left-0 w-full h-full cursor-crosshair z-10"
                  />
                </div>
              )}
            </div>
          </div>

          {/* BookletViewer overlay — opened via "View Answer Sheet" button */}
          {showBooklet && activeEval && (
            <BookletViewer
              pdfUrl={
                activeEval.student_combined_pdf
                  ? getProxyPdfUrl(activeEval.student_assignment)
                  : null
              }
              studentName={activeEval.student_name}
              studentId={activeEval.student_id_num}
              subject={activeEval.bundle_details?.schedule_details?.subject || ''}
              fallbackSheets={activeEval.answer_sheets || []}
              onClose={() => setShowBooklet(false)}
              initialAnnotations={history}
              onAnnotationsChange={(updatedAnnotations) => {
                setHistory(updatedAnnotations);
                autoSaveAnnotations(updatedAnnotations);
              }}
              readOnly={activeEval.status === 'SUBMITTED' || activeEval.status === 'COMPLETED'}
            />
          )}
        </div>
      ) : (
        // ── LIST / DASHBOARD VIEW ──────────────────────────────────────────
        <div className="space-y-6">
          
          {/* Stats overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col justify-between shadow-sm">
              <div className="text-slate-500 text-xs font-bold uppercase tracking-wider">Pending Evaluations</div>
              <div className="text-3xl font-black text-slate-900 mt-2">{stats.pending}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col justify-between shadow-sm">
              <div className="text-slate-500 text-xs font-bold uppercase tracking-wider">Completed Evaluations</div>
              <div className="text-3xl font-black text-slate-900 mt-2">{stats.completed}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col justify-between shadow-sm">
              <div className="text-slate-500 text-xs font-bold uppercase tracking-wider">Total Papers Assigned</div>
              <div className="text-3xl font-black text-slate-900 mt-2">{stats.total}</div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-slate-800">My Answer Sheet Assignments</h2>
              <p className="text-sm text-slate-500">Select an answer sheet below to start evaluation. Audio/video screen recording will be initiated automatically.</p>
            </div>
            <div className="w-full md:w-80 flex gap-2">
              <input
                type="text"
                className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#319ab9] focus:outline-none"
                placeholder="🔍 Search student name, roll no, subject..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12 text-slate-400">Loading assignments...</div>
          ) : filtered.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400">
              {search ? 'No matches found.' : 'No evaluations assigned to you yet.'}
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 text-xs font-bold uppercase tracking-wider">
                    <th className="px-6 py-4">Student Details</th>
                    <th className="px-6 py-4">Subject &amp; Exam</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4">My Marks</th>
                    <th className="px-6 py-4">All Evaluators &amp; Mean</th>
                    <th className="px-6 py-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
                  {filtered.map(a => {
                    const myMarks = (a.status === 'SUBMITTED' || a.status === 'COMPLETED' || a.status === 'SAVED') ? a.total_marks : null;
                    // co-evaluations from bundle details (if available)
                    const coEvals = a.bundle_details?.co_evaluations || [];
                    // Compute mean from submitted (including self)
                    const allSubmitted = [
                      ...(a.status === 'SUBMITTED' || a.status === 'COMPLETED' ? [a.total_marks] : []),
                      ...coEvals.filter(ce => ce.status === 'SUBMITTED').map(ce => ce.total_marks)
                    ];
                    const mean = allSubmitted.length > 0
                      ? (allSubmitted.reduce((s, v) => s + v, 0) / allSubmitted.length).toFixed(2)
                      : null;

                    return (
                      <tr key={a.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="font-semibold text-slate-900">{a.student_name}</div>
                          <div className="text-xs text-slate-500">Roll No: {a.student_id_num || '—'}</div>
                        </td>
                        <td className="px-6 py-4 text-slate-600">
                          <div className="font-medium text-slate-900">{a.bundle_details?.schedule_details?.subject}</div>
                          <div className="text-xs text-slate-500">
                            {a.bundle_details?.schedule_details?.department || ''}
                            {a.bundle_details?.schedule_details?.exam_type_display ? ` · ${a.bundle_details.schedule_details.exam_type_display}` : ''}
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5">{a.bundle_details?.question_paper_details?.title || '—'}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${
                            a.status === 'SUBMITTED' || a.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                            a.status === 'SAVED' || a.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
                            'bg-amber-100 text-amber-800'
                          }`}>
                            {a.status === 'SUBMITTED' ? 'Submitted' :
                             a.status === 'SAVED' ? 'Draft Saved' :
                             a.status === 'PENDING' ? 'Pending' :
                             a.status}
                          </span>
                        </td>
                        {/* My submitted marks */}
                        <td className="px-6 py-4">
                          {(a.status === 'SUBMITTED' || a.status === 'COMPLETED') ? (
                            <div>
                              <div className="text-lg font-black text-slate-900">{a.total_marks}</div>
                              <div className="text-[10px] text-slate-400 font-medium">marks given</div>
                              {a.comments && (
                                <div className="text-[10px] text-slate-500 italic mt-1 max-w-[120px] truncate" title={a.comments}>"{a.comments}"</div>
                              )}
                            </div>
                          ) : a.status === 'SAVED' ? (
                            <div className="text-sm text-blue-600 font-semibold">{a.total_marks || 0} (draft)</div>
                          ) : (
                            <span className="text-slate-400 text-xs">—</span>
                          )}
                        </td>
                        {/* Co-evaluator marks + mean */}
                        <td className="px-6 py-4">
                          {(a.status === 'SUBMITTED' || a.status === 'COMPLETED') && coEvals.length > 0 ? (
                            <div className="space-y-2">
                              <div className="flex flex-wrap gap-1.5">
                                {coEvals.map((ce, idx) => (
                                  <div key={idx} className="text-[10px] bg-slate-50 border border-slate-200 rounded-lg px-2 py-1">
                                    <div className="font-bold text-slate-700">{ce.evaluator_name}</div>
                                    <div className={`font-black ${ce.status === 'SUBMITTED' ? 'text-slate-900' : 'text-slate-400'}`}>
                                      {ce.status === 'SUBMITTED' ? `${ce.total_marks} M` : 'Pending'}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              {mean !== null && (
                                <div className="flex items-center gap-1.5 bg-[#319ab9]/10 border border-[#319ab9]/20 rounded-lg px-2 py-1.5">
                                  <span className="text-[10px] font-bold text-[#319ab9] uppercase">Mean</span>
                                  <span className="text-sm font-black text-[#1a6a80]">{mean}</span>
                                  <span className="text-[9px] text-slate-500">({allSubmitted.length} evaluator{allSubmitted.length !== 1 ? 's' : ''})</span>
                                </div>
                              )}
                            </div>
                          ) : (a.status === 'SUBMITTED' || a.status === 'COMPLETED') ? (
                            <span className="text-[10px] text-slate-400 italic">Awaiting co-evaluators</span>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => handleStartEvaluation(a)}
                            className={`font-bold px-4 py-2 rounded-xl text-xs transition-colors ${
                              a.status === 'SUBMITTED' || a.status === 'COMPLETED'
                                ? 'bg-slate-100 text-slate-500 cursor-not-allowed'
                                : 'bg-[#319ab9] hover:bg-[#28849f] text-white'
                            }`}
                            disabled={a.status === 'SUBMITTED' || a.status === 'COMPLETED'}
                          >
                            {a.status === 'SAVED' || a.status === 'IN_PROGRESS' ? '🔄 Resume Eval' : a.status === 'SUBMITTED' ? '✓ Done' : '🖊 Start Eval'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}

